import { api } from './apiClient';

/**
 * Mirrors the backend's `PublicWatchEntry`. The `id` is server-assigned and
 * absent on entries that were created locally and haven't been synced yet.
 */
export interface WatchEntry {
  id?: string;
  movieId: number | null;
  tvId: number | null;
  animeMalId: number | null;
  sportsVodId: string | null;
  seasonNumber: number | null;
  episodeNumber: number | null;
  animeAudio: 'sub' | 'dub' | null;
  progress: number;
  lastUpdated: string;
}

/**
 * Matches `recordSchema` on the backend: exactly one of movieId, tvId, animeMalId, or sportsVodId.
 * TV requires both seasonNumber + episodeNumber.
 * Anime requires episodeNumber + animeAudio.
 * Movies and sports-VOD forbid season/episode/audio.
 * progress is an int (typically ms watched).
 */
export type RecordWatchInput =
  | {
      movieId: number;
      tvId?: undefined;
      animeMalId?: undefined;
      sportsVodId?: undefined;
      seasonNumber?: undefined;
      episodeNumber?: undefined;
      animeAudio?: undefined;
      progress: number;
      source?: 'history' | 'search';
    }
  | {
      tvId: number;
      movieId?: undefined;
      animeMalId?: undefined;
      sportsVodId?: undefined;
      seasonNumber: number;
      episodeNumber: number;
      animeAudio?: undefined;
      progress: number;
      source?: 'history' | 'search';
    }
  | {
      animeMalId: number;
      movieId?: undefined;
      tvId?: undefined;
      sportsVodId?: undefined;
      episodeNumber: number;
      animeAudio: 'sub' | 'dub';
      seasonNumber?: undefined;
      progress: number;
      source?: 'history' | 'search';
    }
  | {
      sportsVodId: string;
      movieId?: undefined;
      tvId?: undefined;
      animeMalId?: undefined;
      seasonNumber?: undefined;
      episodeNumber?: undefined;
      animeAudio?: undefined;
      progress: number;
      source?: 'history' | 'search';
    };

export const historyApi = {
  list(): Promise<{ entries: WatchEntry[] }> {
    return api<{ entries: WatchEntry[] }>('/api/history', { auth: true });
  },
  record(input: RecordWatchInput): Promise<{ entry: WatchEntry }> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { source: _source, ...body } = input as RecordWatchInput & { source?: 'history' | 'search' };
    return api<{ entry: WatchEntry }>('/api/history', {
      method: 'POST',
      body,
      auth: true,
    });
  },
};
